/* Pick-or-add against a controlled list (designs/intake-unified-form.md
   UF15/UF21): type, pick a match, or add the typed name on the spot. Near
   matches are listed first so a duplicate takes a deliberate click. One
   control for teams AND products — the storage differs, the behaviour must
   not. Entra is never consulted (Trevor: "Entra is chaos right now"). */
import { Fragment, useEffect, useId, useRef, useState, type JSX } from "react";
import type { FieldHelp } from "./form-help";
import { useDismiss } from "./useDismiss";

export interface PickItem {
  id: string;
  name: string;
  retired?: boolean;
}

/* Levenshtein ≤ 1, or a case-insensitive prefix/substring — the "did you
   mean" band. Pure, so it is unit-testable without a DOM. */
export const nearMatch = (typed: string, name: string): boolean => {
  const a = typed.trim().toLowerCase();
  const b = name.trim().toLowerCase();
  if (a === "" || b === "") return false;
  if (b.includes(a) || a.includes(b)) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  /* one edit: substitution, insertion or deletion */
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i++;
      j++;
      continue;
    }
    if (++edits > 1) return false;
    if (a.length > b.length) i++;
    else if (b.length > a.length) j++;
    else {
      i++;
      j++;
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
};

/* the ordered suggestion list: exact match first, then near matches. NOT
   capped (UF15-a): a cap with no sign of the cut hid most of the list, and a
   requester who cannot see their team adds a near-duplicate of it. */
export const rankItems = <T extends PickItem>(typed: string, items: T[]): T[] => {
  const q = typed.trim().toLowerCase();
  const live = items.filter((t) => !t.retired);
  if (q === "") return live;
  const exact = live.filter((t) => t.name.toLowerCase() === q);
  const near = live.filter((t) => !exact.includes(t) && nearMatch(q, t.name));
  return [...exact, ...near];
};

export function PickOrAdd<T extends PickItem>({
  label,
  noun,
  items,
  value,
  onChange,
  onAdd,
  onAdded,
  disabled = false,
  testId,
  groupOf,
  help,
}: {
  label: string;
  /* "team" / "product" — the word in "Add “X” as a new team" */
  noun: string;
  items: T[];
  /* the picked id, or "" for nothing picked */
  value: string;
  onChange: (id: string) => void;
  /* creates the item on the server; the parent refreshes its list via
     onAdded so every other picker on the page sees it too. Omitted = a
     pick-only list (the Product field — teams-and-products.md TP8) */
  onAdd?: (name: string) => Promise<T>;
  onAdded?: (item: T) => void;
  disabled?: boolean;
  /* data-* hook the tests and e2e address the control by */
  testId: string;
  /* the heading an item sits under while nothing is typed (UF15-a); the
     items must already arrive in group order. Omitted = a flat list. */
  groupOf?: (item: T) => string;
  /* the field's hint + hover (intake-form-help.md FH4); omitted = none */
  help?: FieldHelp;
}): JSX.Element {
  const picked = items.find((t) => t.id === value) ?? null;
  const [typed, setTyped] = useState("");
  const [open, setOpen] = useState(false);
  const dismiss = useDismiss(open, setOpen);
  const hintId = useId();
  /* "change" unmounts the button that held focus; the reopened input
     takes it, so the menu's Escape and Tab handling reach the picker
     rather than the drawer behind it (Copilot on #145) */
  const inputRef = useRef<HTMLInputElement>(null);
  const [focusInput, setFocusInput] = useState(false);
  useEffect(() => {
    if (!focusInput) return;
    inputRef.current?.focus();
    setFocusInput(false);
  }, [focusInput]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ranked = rankItems(typed, items);
  /* typed = ranked by match, so list-order headings would mislabel it */
  const headOf = (i: number): string | null => {
    if (groupOf === undefined || typed.trim() !== "") return null;
    const group = groupOf(ranked[i]!);
    return i === 0 || groupOf(ranked[i - 1]!) !== group ? group : null;
  };
  const exact = ranked.find((t) => t.name.toLowerCase() === typed.trim().toLowerCase());
  const canAdd = onAdd !== undefined && typed.trim().length >= 2 && exact === undefined;
  const hook = { [`data-${testId}`]: true } as Record<string, boolean>;
  const addHook = { [`data-${testId}-add`]: true } as Record<string, boolean>;

  const add = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      if (onAdd === undefined) return;
      const created = await onAdd(typed.trim());
      onAdded?.(created);
      onChange(created.id);
      setTyped("");
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (picked !== null)
    return (
      <div className="capture-field" {...hook}>
        <span title={help?.more}>{label}</span>
        <div className="picker-picked">
          <span className="picker-chip">{picked.name}</span>
          <button
            type="button"
            className="link-inline"
            aria-describedby={help === undefined ? undefined : hintId}
            disabled={disabled}
            onClick={() => {
              onChange("");
              setOpen(true);
              setFocusInput(true);
            }}
          >
            change
          </button>
        </div>
        {/* the guidance stays once the field is filled (FH4, Copilot on #146) */}
        {help !== undefined && (
          <small className="capture-hint" id={hintId}>
            {help.hint}
          </small>
        )}
      </div>
    );

  return (
    <div className="capture-field" {...hook} {...dismiss}>
      <label>
        <span title={help?.more}>{label}</span>
        <input
          ref={inputRef}
          value={typed}
          aria-describedby={help === undefined ? undefined : hintId}
          disabled={disabled || busy}
          placeholder={`type a ${noun} name`}
          autoComplete="off"
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setTyped(e.target.value);
            setOpen(true);
          }}
        />
      </label>
      {help !== undefined && (
        <small className="capture-hint" id={hintId}>
          {help.hint}
        </small>
      )}
      {open && (
        <div className="picker-menu" role="listbox" aria-label={`${label} options`}>
          {ranked.map((t, i) => {
            const head = headOf(i);
            return (
              <Fragment key={t.id}>
                {head !== null && (
                  <div className="picker-group" role="presentation" data-picker-group>
                    {head}
                  </div>
                )}
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="picker-option"
                  onClick={() => {
                    onChange(t.id);
                    setTyped("");
                    setOpen(false);
                  }}
                >
                  {t.name}
                </button>
              </Fragment>
            );
          })}
          {canAdd && (
            <button
              type="button"
              className="picker-option picker-add"
              disabled={busy}
              onClick={() => void add()}
              {...addHook}
            >
              {busy ? "Adding…" : `Add “${typed.trim()}” as a new ${noun}`}
            </button>
          )}
          {ranked.length === 0 && !canAdd && (
            <div className="kv-note">
              {onAdd === undefined ? `No ${noun} matches.` : "Keep typing — two letters at least."}
            </div>
          )}
        </div>
      )}
      {error !== null && <p className="capture-error">{error}</p>}
    </div>
  );
}
